import { Segmented } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import classes from './FileSettings.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDocsSegment } from 'store/slices/windowStateSlice';
import { fetchAgreements } from 'store/reducers/agreementsCreator';
import { fetchSigningState } from 'store/reducers/signingCreator';
import { SegmentedOptions } from 'antd/es/segmented';
import { useNavigate } from 'react-router';
import { setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import SingingTab from 'components/DetailsComponent/SingingTab/SingingTab';
import AgreementTab from 'components/DetailsComponent/ArgeementTab/AgreementTab';
import DeclineAgreementModal from 'components/DetailsComponent/ArgeementTab/DeclineAgreementModal';
import { setDocumentToViewNull } from 'store/slices/viewerSlice';
import { typesToSigning } from 'helpers/filesExtensions';
import { cleanDocumentViewUrl } from 'helpers/documentFiltersUrl';
import api from 'api';

type FileSettingsProps = {
  currentKey: string
};

const FileSettings = ({currentKey}: FileSettingsProps) => {  
  const [activeKey, setActiveKey] = React.useState(currentKey);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = React.useState<boolean>(false);
  const [loadingUsersPermissions, setLoadingUsersPermissions] = React.useState(false);
  const [usersWithPermissions, setUsersWithPermissions] = React.useState<any[]>([]);

  const { selectedDocument, dataDocumentTable } = useAppSelector((state) => state.documents);
  const { agreements } = useAppSelector((state) => state.agreements);
  const { signing } = useAppSelector((state) => state.signing);
  const { userPermissions } = useAppSelector((state) => state.userData);

  const documentPermissions = dataDocumentTable.find(({ id }) => id === selectedDocument?.id)?.permissions!;
  
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    setActiveKey(currentKey);
    if (currentKey === 'agreement' && !agreements[0]) {
      dispatch(fetchAgreements({id: selectedDocument?.id!, entity: 'document'}));
    };
 
  }, [currentKey]);

  React.useEffect(() => {
    dispatch(setDocsSegment('view'));
    if (activeKey === 'agreement' && !agreements[0]) {
      dispatch(fetchAgreements({id: selectedDocument?.id!, entity: 'document'}));
    };

  }, [activeKey])

  React.useEffect(() => {
    if ((currentKey === 'signing' && !signing[0]) || (activeKey === 'signing' && !signing[0])) {
    selectedDocument?.signing_status !== null && dispatch(fetchSigningState({id: selectedDocument?.id!}));
  };
}, [activeKey, currentKey]);

React.useEffect(() => {
  if ((activeKey === 'agreement' && !agreements[0]) || (currentKey === 'agreement' && !agreements[0])) {
      dispatch(fetchAgreements({id: selectedDocument?.id!, entity: 'document'}));
    };
  }, [activeKey, currentKey]);

  React.useEffect(() => {
    getUsersPermissions();
  },[]);
  
  const getUsersPermissions = async () => {
    setLoadingUsersPermissions(true);
    try{
      const resp = await api.getUsersByFilter(JSON.stringify(
        [{'field':'by_document','value': selectedDocument?.id}]
      ));
      
      setUsersWithPermissions(resp.data.map(item => ({...item, ...item.user})));
    } catch (e) {} finally {
      setLoadingUsersPermissions(false);
    }
  }

  const onDeclineOpen = () => setIsDeclineModalOpen(true);
  const onDeclineCancel = () => setIsDeclineModalOpen(false);

  const segmentItems: SegmentedOptions<string> = [];

  userPermissions.can_manage_signing && typesToSigning.includes(selectedDocument?.extension?.toLocaleLowerCase()!) && segmentItems.push({
    value: 'signing',
    label: t('Documents.details.signing.title'),
  });

  userPermissions.can_edit_agreement && segmentItems.push({
    value: 'agreement',
    label: t('Documents.details.agreement.agreement'),
  });

  const returnToAllDocs = () => {
    dispatch(setViewType('Table'));
    dispatch(setSelectedDocument(null));
    dispatch(setDocumentToViewNull());
    navigate(cleanDocumentViewUrl(window.location.pathname, new URLSearchParams(window.location.search)));
  }

  const contentSetting = React.useMemo(
    () => ({
      signing:
        userPermissions.can_manage_signing &&
        typesToSigning.includes(
          selectedDocument?.extension?.toLocaleLowerCase()!
        ) ? (
          <SingingTab
            closeDetailsModal={returnToAllDocs}
            usersWithPermissions={usersWithPermissions}
            loadingUsersPermissions={loadingUsersPermissions}
          />
        ) : null,
      agreement: userPermissions.can_edit_agreement ? (
        <AgreementTab 
          onDeclineOpen={onDeclineOpen}
          usersWithPermissions={usersWithPermissions}
          loadingUsersPermissions={loadingUsersPermissions}
        />
      ) : null,
    }),
    [userPermissions, selectedDocument, usersWithPermissions]
  );

  return (
    <div>
      <Segmented
        className={classes.tabs}
        options={segmentItems}
        block
        selected
        value={activeKey || 'signing'}
        onChange={setActiveKey}
      />

      <div className={classes.content}>
        {contentSetting[activeKey as keyof typeof contentSetting]}
      </div>

      {isDeclineModalOpen && (
        <DeclineAgreementModal
          t={t}
          documentPermissions={documentPermissions}
          open={isDeclineModalOpen}
          onCancel={onDeclineCancel}
          documentId={selectedDocument?.id!}
        />
      )}
    </div>
  );
}

export default FileSettings;
